/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useMemo } from 'react';

import { css, jsx } from '@compiled/react';
import type { IntlShape } from 'react-intl';
import { IntlProvider } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import AiGenerativeTextIcon from '@atlaskit/icon-lab/core/ai-generative-text';
import { token } from '@atlaskit/tokens';

import type { AiSuggestionsPlugin, Suggestion } from '../../aiSuggestionsPluginType';
import { aiSuggestionsPluginKey } from '../../pm-plugins/main';
import { getInversion } from '../utils/getInversion';

const buttonContainer = css({
	position: 'absolute',
	top: 0,
	right: token('space.negative.250'),
	zIndex: 1,
	transform: 'translateX(100%)',
});

export function SuggestionIconButton({
	suggestion,
	suggestionsCount,
	isSelected,
	api,
	intl,
}: {
	api?: ExtractInjectionAPI<AiSuggestionsPlugin>;
	intl?: IntlShape;
	isSelected: boolean;
	suggestion: Suggestion;
	suggestionsCount: number;
}) {
	const { baseLocalIdBySuggestionId } = useSharedPluginStateWithSelector(
		api,
		['aiSuggestions'],
		(state) => {
			const { baseLocalIdBySuggestionId } = state?.aiSuggestionsState ?? {};

			return {
				baseLocalIdBySuggestionId,
			};
		},
	);

	const onSelectSuggestion = () => {
		api?.core?.actions.execute(({ tr }) => {
			const invertedTr = getInversion(tr, suggestion);
			api?.showDiff?.commands.showDiff({
				steps: invertedTr.steps.reverse().map((step) => step.invert(invertedTr.before)),
				originalDoc: invertedTr.doc,
				isInverted: true,
				diffType: 'step',
			})({ tr });

			tr.setMeta(aiSuggestionsPluginKey, {
				action: 'SELECT_SUGGESTION',
				id: suggestion.id,
			});
			return tr;
		});
	};

	const tooltip = useMemo(
		() => ({
			content:
				suggestionsCount > 1
					? intl?.formatMessage(aiSuggestionsMessages.iconButtonSuggestionsCountLabel, {
							count: suggestionsCount,
						})
					: suggestion.shortDescription,
			position: 'right' as const,
		}),
		[intl, suggestion.shortDescription, suggestionsCount],
	);

	return (
		<IntlProvider
			defaultLocale={intl?.defaultLocale || 'en-US'}
			locale={intl?.locale || 'en-US'}
			messages={intl?.messages}
			formats={intl?.formats}
		>
			<span
				css={buttonContainer}
				style={{
					anchorName: `--button-${baseLocalIdBySuggestionId?.get(suggestion.id)}`,
				}}
			>
				{
					// We keep the anchorName span rendered so that the suggestion card can reference it
					!isSelected && (
						<IconButton
							isTooltipDisabled={false}
							tooltip={tooltip}
							icon={AiGenerativeTextIcon}
							appearance="subtle"
							label=""
							spacing="compact"
							onClick={onSelectSuggestion}
						/>
					)
				}
			</span>
		</IntlProvider>
	);
}
